import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Save, Upload, Trash2, Loader2, Inbox } from "lucide-react";
import { listConfigs, saveConfig, deleteConfig } from "@/lib/api";
import { toast } from "sonner";

export const SavedConfigsSheet = ({ open, onOpenChange, currentConfig, onLoad }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listConfigs();
      setItems(data);
    } catch {
      toast.error("Impossibile caricare le configurazioni");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  const handleSave = async () => {
    const finalName = name.trim() || currentConfig?.botName || "Configurazione XAUUSD";
    setSaving(true);
    try {
      await saveConfig(finalName, currentConfig);
      setName("");
      toast.success(`Salvata: ${finalName}`);
      refresh();
    } catch {
      toast.error("Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setItems((prev) => prev.filter((x) => x.id !== id)); // optimistic update
    try {
      await deleteConfig(id);
      toast.success("Configurazione eliminata");
    } catch {
      toast.error("Eliminazione non riuscita");
      refresh();
    }
  };

  const handleLoad = (item) => {
    onLoad(item.config);
    toast.success(`Caricata: ${item.name}`);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col" data-testid="saved-configs-sheet">
        <SheetHeader>
          <SheetTitle>Configurazioni salvate</SheetTitle>
          <SheetDescription>
            Salva la configurazione corrente o caricane una esistente.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex items-center gap-2">
          <Input
            data-testid="save-config-name-input"
            placeholder="Nome configurazione"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button data-testid="save-config-button" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva
          </Button>
        </div>

        <Separator className="my-4" />

        <ScrollArea className="flex-1 -mx-2 px-2">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
              <Inbox className="h-8 w-8 mb-2 opacity-60" />
              <p className="text-sm">Nessuna configurazione salvata</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="saved-configs-list">
              {items.map((item) => (
                <div
                  key={item.id}
                  data-testid="saved-config-item"
                  className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3 hover:bg-muted/40 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{item.name}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">
                      {new Date(item.updated_at).toLocaleString("it-IT")}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="load-config-button"
                      onClick={() => handleLoad(item)}
                    >
                      <Upload className="h-3.5 w-3.5 mr-1" /> Carica
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" data-testid="delete-config-button" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminare la configurazione?</AlertDialogTitle>
                          <AlertDialogDescription>
                            "{item.name}" verrà eliminata definitivamente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction
                            data-testid="confirm-delete-config"
                            onClick={() => handleDelete(item.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Elimina
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
